import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type Certification,
  useCreateCertification,
  useUpdateCertification,
} from "@/hooks/use-certifications";
import {
  type CertificationCreateInput,
  certificationCreateSchema,
  certificationStatusValues,
} from "@/schemas/certifications";

export function CertificationFormDialog({
  open,
  onOpenChange,
  certification,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certification?: Certification;
}) {
  const isEdit = certification != null;
  const create = useCreateCertification();
  const update = useUpdateCertification(certification?.id ?? "");
  // Three generics, not one: the schema has `.default()` fields, so its INPUT
  // type has them optional while its OUTPUT type has them required. Passing
  // only the output type makes the resolver's own input type unassignable.
  const form = useForm<
    z.input<typeof certificationCreateSchema>,
    unknown,
    CertificationCreateInput
  >({
    resolver: zodResolver(certificationCreateSchema),
    defaultValues: {
      name: certification?.name ?? "",
      issuingBody: certification?.issuingBody ?? "",
      description: certification?.description ?? "",
      status: certification?.status ?? "draft",
    },
  });

  // Route every close (cancel/escape/backdrop) through a reset so reopening
  // never shows a previous session's abandoned input.
  function handleOpenChange(next: boolean) {
    if (!next) form.reset();
    onOpenChange(next);
  }

  async function onSubmit(values: CertificationCreateInput) {
    const payload = {
      ...values,
      issuingBody: values.issuingBody?.trim() || null,
      description: values.description?.trim() || null,
    };
    if (isEdit) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit certification" : "Add certification"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Change this certification's details."
              : "Add a certification so it can be assigned to people and required by a role."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. AWS Solutions Architect Associate" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="issuingBody"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Issuing body</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} placeholder="e.g. AWS" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      rows={3}
                      placeholder="Optional summary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {certificationStatusValues.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
